import {Group, Stack, Stepper, Text} from '@mantine/core';
import React, {useReducer, useMemo, useCallback} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import type {RunModalProps} from 'ui-library/runModal';

import ConfigurePane from './ConfigurePane';
import FileUploadPane from './FileUploadPane';
import moleculeImportReducer, {INITIAL_STATE, ModalStep} from './moleculeImportReducer';

const STEPPER_STEPS: Array<{key: ModalStep; label: string}> = [
  {key: 'CONFIGURE', label: 'Configure'},
  {key: 'FILE_UPLOAD', label: 'Upload file'},
  {key: 'MAP_COLUMNS', label: 'Map columns'},
];

interface ModalFooterProps {
  currentStepIndex: number;
  step: ModalStep;
  isNextDisabled: boolean;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
}

function ModalFooter({
  currentStepIndex,
  step,
  isNextDisabled,
  onBack,
  onNext,
  onCancel,
}: ModalFooterProps): JSX.Element {
  return (
    <Group position="apart">
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        onClick={currentStepIndex === 0 ? onCancel : onBack}
      >
        {currentStepIndex === 0 ? 'Cancel' : 'Back'}
      </Button>
      {step === 'MAP_COLUMNS' ? (
        <Button buttonStyle={ButtonStyle.PRIMARY} size={ButtonSize.MEDIUM} disabled={true}>
          Import
        </Button>
      ) : (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          onClick={onNext}
          disabled={isNextDisabled}
        >
          Next
        </Button>
      )}
    </Group>
  );
}

interface MoleculeImportModalProps extends RunModalProps<void> {
  initialFolderId?: string | null;
}

export default function MoleculeImportModal({close, initialFolderId}: MoleculeImportModalProps): JSX.Element {
  const [state, dispatch] = useReducer(moleculeImportReducer, {
    ...INITIAL_STATE,
    initialFolderId: initialFolderId ?? null,
  });

  const currentStepIndex = useMemo(() => {
    const index = STEPPER_STEPS.findIndex((s) => s.key === state.step);
    return index === -1 ? STEPPER_STEPS.length : index;
  }, [state.step]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex - 1].key}});
    }
  }, [currentStepIndex]);

  const handleNext = useCallback(() => {
    if (currentStepIndex < STEPPER_STEPS.length - 1) {
      dispatch({type: 'GO_TO_STEP', payload: {step: STEPPER_STEPS[currentStepIndex + 1].key}});
    }
  }, [currentStepIndex]);

  const isNextDisabled = useMemo(() => {
    switch (state.step) {
      case 'CONFIGURE':
        return (
          !state.schemaId ||
          state.registrationConfig == null ||
          !state.registrationConfig.registryId ||
          !state.registrationConfig.namingStrategy
        );
      case 'FILE_UPLOAD':
        return state.headers == null;
      case 'MAP_COLUMNS':
        return false;
      default:
        return true;
    }
  }, [state.step, state.schemaId, state.headers, state.registrationConfig]);

  const renderStepContent = (): JSX.Element => {
    switch (state.step) {
      case 'CONFIGURE':
        return <ConfigurePane state={state} dispatch={dispatch} />;
      case 'FILE_UPLOAD':
        return <FileUploadPane state={state} dispatch={dispatch} />;
      case 'MAP_COLUMNS':
        return <Text>Column mapping will go here.</Text>;
      case 'SUBMITTING':
        return <Text>Submitting...</Text>;
      case 'DONE':
        return <Text>Done!</Text>;
      default:
        return <Text>Unknown step</Text>;
    }
  };

  return (
    <Stack spacing="lg">
      <Stepper active={currentStepIndex} allowNextStepsSelect={false} breakpoint="sm">
        {STEPPER_STEPS.map((step) => (
          <Stepper.Step key={step.key} label={step.label} />
        ))}
      </Stepper>

      {renderStepContent()}

      <ModalFooter
        currentStepIndex={currentStepIndex}
        step={state.step}
        isNextDisabled={isNextDisabled}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={() => close()}
      />
    </Stack>
  );
}
